import styled from 'styled-components';
import { Carousel, Button } from 'antd';
import { colors, fonts, sizes, breakpoints } from '../../theme';
import { StyledCarouselProps, SteamButtonProps } from './GameSlide.types';

export const GameSlideContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
`;

export const StyledCarousel = styled(Carousel)<StyledCarouselProps>`
  .ant-carousel {
    overflow: hidden;
  }

  ${breakpoints.media.mobile} {
    margin: 0 !important;
    padding: 0 !important;
    
    .ant-carousel {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .slick-list {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .slick-track {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .slick-slide {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .slick-slide > div {
      margin: 0 !important;
      padding: 0 !important;
    }
  }

  .ant-carousel .slick-slide {
    text-align: center;
    background: transparent;
  }

  .ant-carousel .slick-dots {
    bottom: ${sizes.spacing.lg};
    z-index: 10;

    li {
      margin: 0 ${sizes.pixels.small};

      button {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: ${colors.whiteTransparent[50]};
        border: 2px solid ${colors.white};
        opacity: 0.6;
        transition: all 0.3s ease;

      }

      &.slick-active button {
        background: ${colors.primary};
        opacity: 1;
        box-shadow: 0 0 ${sizes.pixels.medium} ${colors.primaryTransparent[50]};
      }
    }

    ${breakpoints.media.mobile} {
      bottom: ${sizes.spacing.md};

      li button {
        width: 10px;
        height: 10px;
      }
    }
  }

  .ant-carousel .slick-arrow,
  .slick-arrow {
    z-index: 15 !important;
    width: 48px !important;
    height: 48px !important;
    background: ${colors.primary} !important;
    border: 2px solid ${colors.primary} !important;
    border-radius: 50% !important;
    transition: opacity 0.2s ease;
    cursor: pointer;
    opacity: 1 !important;
    box-shadow: 0 2px 8px ${colors.blackTransparent[30]} !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 20px !important;
    color: ${colors.white} !important;
    font-weight: bold;
    font-family: Arial, sans-serif;
    line-height: 1;
    
    &:before,
    &:after {
      display: none !important;
    }
    
    &:hover {
      opacity: 1 !important;
    }

    &:focus {
      outline: none;
      opacity: 1 !important;
      box-shadow:
        0 2px 8px ${colors.blackTransparent[30]},
        0 0 0 2px ${colors.whiteTransparent[50]} !important;
    }

    &:active {
      opacity: 0.9;
    }

    ${breakpoints.media.mobile} {
      width: 38px !important;
      height: 38px !important;
      font-size: 16px !important;
    }
  }

  .ant-carousel .slick-prev {
    left: ${sizes.spacing.xl};

    ${breakpoints.media.mobile} {
      left: ${sizes.spacing.md};
    }
  }

  .ant-carousel .slick-next {
    right: ${sizes.spacing.xl};

    ${breakpoints.media.mobile} {
      right: ${sizes.spacing.md};
    }
  }
`;

export const GameSlideItem = styled.div`
  position: relative;
  width: 100%;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;

  ${breakpoints.media.mobile} {
    height: calc(100vw * 0.5625); /* 16:9 aspect ratio */
    max-height: 50vh;
    min-height: 250px;
    margin: 0 !important;
    padding: 0 !important;
    top: 0 !important;
  }
`;

export const GameImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const GameImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

export const GameContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${sizes.padding.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.gap.md};
  z-index: 5;

  ${breakpoints.media.mobile} {
    padding: ${sizes.padding.md};
    gap: ${sizes.gap.sm};
  }
`;

export const GameTitle = styled.h2`
  color: ${colors.white};
  font-family: ${fonts.families.primary};
  font-size: ${fonts.sizes.xl};
  font-weight: ${fonts.weights.bold};
  text-transform: ${fonts.textTransform.uppercase};
  letter-spacing: ${fonts.letterSpacing.wide};
  text-shadow: 0 0 ${sizes.pixels.medium} ${colors.blackTransparent[90]};
  margin: 0;
  text-align: center;

  ${breakpoints.media.desktop} {
    font-size: ${fonts.sizes.desktopXxl};
  }

  ${breakpoints.media.mobile} {
    font-size: ${fonts.sizes.lg};
  }
`;

export const SteamButton = styled(Button)<SteamButtonProps>`
  display: flex;
  align-items: center;
  gap: ${sizes.gap.sm};
  padding: ${sizes.padding.md} ${sizes.padding.lg};
  height: auto;
  background: ${colors.primaryTransparent[80]};
  border: none;
  border-radius: ${sizes.borderRadius.medium};
  color: ${colors.white};
  font-family: ${fonts.families.primary};
  font-size: ${fonts.sizes.md};
  font-weight: ${fonts.weights.bold};
  text-transform: ${fonts.textTransform.uppercase};
  letter-spacing: ${fonts.letterSpacing.normal};
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 12px ${colors.blackTransparent[40]};

  ${breakpoints.media.desktop} {
    font-size: ${fonts.sizes.desktopLg};
  }

  &:hover {
    background: ${colors.primary};
    color: ${colors.white};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${colors.primaryTransparent[40]};
    text-shadow: 0 0 ${sizes.pixels.small} ${colors.whiteTransparent[50]};
  }

  &:focus {
    background: ${colors.primary};
    color: ${colors.white};
    outline: 2px solid ${colors.white};
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px ${colors.primaryTransparent[50]};
  }

  ${breakpoints.media.mobile} {
    padding: ${sizes.padding.sm} ${sizes.padding.md};
    font-size: ${fonts.sizes.sm};
    gap: ${sizes.gap.xs};
  }
`;

export const SteamIcon = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  width: 24px;
  height: 24px;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: brightness(1.2) contrast(1.1);
    transition: opacity 0.3s ease;
  }

  .logo-basic {
    opacity: 1;
  }

  .logo-hover {
    opacity: 0;
  }

  ${SteamButton}:hover & {
    .logo-basic {
      opacity: 0;
    }

    .logo-hover {
      opacity: 1;
    }
  }

  ${SteamButton}:focus & {
    .logo-basic {
      opacity: 0;
    }

    .logo-hover {
      opacity: 1;
    }
  }

  ${breakpoints.media.mobile} {
    width: 20px;
    height: 20px;
  }
`;

export const SlideAccessibilityLabel = styled.span`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;
